// src/components/communications/MessageItem.js
import React, { useState } from 'react';
import styled from 'styled-components';
import { 
  FaEnvelope, 
  FaEnvelopeOpen, 
  FaLongArrowAltRight, 
  FaLongArrowAltLeft,
  FaAngleDown,
  FaAngleUp,
  FaImage,
  FaVideo,
  FaVolumeUp,
  FaFile,
  FaDownload,
  FaExpand
} from 'react-icons/fa';
import { formatDateTime } from '../../modules/communications/utils/formatUtils';

const ItemContainer = styled.div`
  background-color: ${props => props.theme.colors.surface};
  border-radius: ${props => props.theme.borderRadius};
  padding: ${props => props.theme.spacing(2)};
  margin-bottom: ${props => props.theme.spacing(2)};
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  transition: transform 0.2s;

  &:hover {
    transform: translateX(2px);
  }
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${props => props.theme.spacing(1)};
`;

const DirectionIndicator = styled.div`
  display: flex;
  align-items: center;
  color: ${props => props.theme.colors.primary};
  font-weight: ${props => props.theme.typography.fontWeight.medium};
  
  svg {
    margin-right: ${props => props.theme.spacing(1)};
  }
`;

const StatusBadge = styled.span`
  background-color: ${props => {
    switch (props.status) {
      case 'delivered': return props.theme.colors.success + '22';
      case 'failed':
      case 'undelivered': return props.theme.colors.error + '22';
      case 'received': return props.theme.colors.success + '22';
      default: return props.theme.colors.primary + '22';
    }
  }};
  color: ${props => {
    switch (props.status) {
      case 'delivered': return props.theme.colors.success;
      case 'failed':
      case 'undelivered': return props.theme.colors.error;
      case 'received': return props.theme.colors.success;
      default: return props.theme.colors.primary;
    }
  }};
  padding: ${props => props.theme.spacing(0.5)} ${props => props.theme.spacing(1)};
  border-radius: 12px;
  font-size: 0.8rem;
`;

const Details = styled.div`
  margin: ${props => props.theme.spacing(1)} 0;
  font-size: 0.9rem;
  color: ${props => props.theme.colors.textSecondary};
`;

const Detail = styled.div`
  margin-bottom: ${props => props.theme.spacing(0.5)};
  display: flex;
  align-items: center;
`;

const MessageContent = styled.div`
  margin-top: ${props => props.theme.spacing(2)};
  padding: ${props => props.theme.spacing(2)};
  background-color: ${props => props.theme.colors.background};
  border-radius: ${props => props.theme.borderRadius};
  position: relative;
  overflow: hidden;
  max-height: ${props => props.expanded ? '500px' : '100px'};
  transition: max-height 0.3s ease;
`;

const MessageText = styled.div`
  white-space: pre-wrap;
  color: ${props => props.theme.colors.textPrimary};
`;

const ExpandButton = styled.button`
  position: absolute;
  bottom: 0;
  right: 0;
  background: linear-gradient(
    to right,
    transparent,
    ${props => props.theme.colors.background} 40%
  );
  border: none;
  padding: ${props => props.theme.spacing(0.5)} ${props => props.theme.spacing(1)};
  color: ${props => props.theme.colors.primary};
  cursor: pointer;
  display: flex;
  align-items: center;
  font-size: 0.8rem;
  opacity: ${props => props.visible ? 1 : 0};
  transition: opacity 0.3s ease;

  svg {
    margin-left: ${props => props.theme.spacing(0.5)};
  }
`;

const MediaGallery = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${props => props.theme.spacing(2)};
  margin-top: ${props => props.theme.spacing(2)};
`;

const MediaItem = styled.a`
  display: block;
  width: 120px;
  height: 120px;
  overflow: hidden;
  border-radius: ${props => props.theme.borderRadius};
  border: 1px solid ${props => props.theme.colors.inputGray}33;
  transition: transform 0.2s ease;

  &:hover {
    transform: scale(1.05);
  }

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const AudioPlayer = styled.div`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing(1)};
  background: ${props => props.theme.colors.background};
  border-radius: ${props => props.theme.borderRadius};
  padding: ${props => props.theme.spacing(1.5)};
  border-left: 3px solid ${props => props.theme.colors.primary};

  .audio-icon {
    color: ${props => props.theme.colors.primary};
    font-size: 1.2rem;
    flex-shrink: 0;
  }

  audio {
    flex: 1;
    min-width: 0;
    height: 36px;
  }
`;

const isAudioUrl = (url) => {
  const ext = url.split('.').pop().split('?')[0].toLowerCase();
  return ['amr', 'ogg', 'mp3', 'wav', 'm4a', '3gp', 'aac', 'mp4'].includes(ext);
};

const MessageItem = ({ message }) => {
  const [expanded, setExpanded] = useState(false);
  const [showExpandButton, setShowExpandButton] = useState(false);
  const URL_API = process.env.REACT_APP_API_PUBLIC || "https://api.mrpatchasphalt.com";

  const mediaPaths = message.mediaPaths || message.media_paths || [];

  // Check if message is long enough to need expansion
  const checkOverflow = (target) => {
    if (!target) return;
    setShowExpandButton(target.scrollHeight > target.clientHeight);
  };

  return (
    <ItemContainer>
      <Header>
        <DirectionIndicator>
          {message.direction === 'inbound' ? (
            <>
              <FaLongArrowAltLeft />
              Incoming Message
            </>
          ) : (
            <>
              <FaLongArrowAltRight />
              Outgoing Message
            </>
          )}
        </DirectionIndicator>
        <StatusBadge status={message.status}>
          {message.status.charAt(0).toUpperCase() + message.status.slice(1)}
        </StatusBadge>
      </Header>

      <Details>
        <Detail>
          <strong>Time:</strong>&nbsp;{formatDateTime(message.timestamp)}
        </Detail>
        <Detail>
          <strong>From:</strong>&nbsp;{message.from}
        </Detail>
        <Detail>
          <strong>To:</strong>&nbsp;{message.to}
        </Detail>
      </Details>

      <MessageContent
        expanded={expanded}
        ref={checkOverflow}
      >
        <MessageText>{message.body}</MessageText>
        <ExpandButton
          visible={showExpandButton}
          onClick={() => setExpanded(!expanded)}
        >
          {expanded ? (
            <>Show Less <FaAngleUp /></>
          ) : (
            <>Show More <FaAngleDown /></>
          )}
        </ExpandButton>
      </MessageContent>

      {mediaPaths.length > 0 && (
        <MediaGallery>
          {mediaPaths.map((url, idx) => (
            isAudioUrl(url) ? (
              <AudioPlayer key={idx}>
                <FaVolumeUp className="audio-icon" />
                <audio controls preload="metadata">
                  <source src={`${URL_API}${url}`} />
                  Your browser does not support audio.
                </audio>
              </AudioPlayer>
            ) : (
              <MediaItem
                key={idx}
                href={`${URL_API}${url}`}
                target="_blank"
                rel="noopener noreferrer"
              >
                <img src={`${URL_API}${url}`} alt={`Media ${idx + 1}`} />
              </MediaItem>
            )
          ))}
        </MediaGallery>
      )}
    </ItemContainer>
  );
};

export default MessageItem;